import * as Sentry from '@sentry/nextjs';
import getConfig from 'next/config';

export interface LogContext {
  [key: string]: any;
}

export interface ILogger {
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: Error, context?: LogContext) => void;
}

class PeikkoLogger implements ILogger {
  private showLogs: boolean;

  constructor() {
    // Get SOMIS_ENV from Next.js config (works in both server and client)
    let somisEnv: string | undefined;

    try {
      const { publicRuntimeConfig } = getConfig() || {};
      somisEnv = publicRuntimeConfig?.SOMIS_ENV || process.env.SOMIS_ENV;
    } catch (e) {
      // Fallback to process.env if getConfig fails (e.g., during build)
      somisEnv = process.env.SOMIS_ENV;
    }

    const normalizedEnv = somisEnv?.toLowerCase();
    this.showLogs = normalizedEnv === 'dev' || normalizedEnv === 'test';

    // Debug: show what environment was detected (always log this)
    console.log(
      `[PEIKKO LOGGER] SOMIS_ENV=${somisEnv || '(not set)'}, showLogs=${
        this.showLogs
      }`,
    );
  }

  private sanitizeContext(context?: LogContext): LogContext {
    if (!context) return {};

    const sanitized = { ...context };

    // Sanitize sensitive data
    if (sanitized.voucherCode) {
      sanitized.voucherCode = this.sanitizeVoucherCode(
        sanitized.voucherCode as string,
      );
    }
    if (sanitized.token) {
      sanitized.token = '[REDACTED]';
    }
    if (sanitized.user?.email) {
      sanitized.user.email = this.sanitizeEmail(sanitized.user.email);
    }

    return sanitized;
  }

  private sanitizeVoucherCode(code: string): string {
    if (!code || code.length < 4) return '[REDACTED]';
    return `${code.substring(0, 2)}***${code.substring(code.length - 2)}`;
  }

  private sanitizeEmail(email: string): string {
    const [local, domain] = email.split('@');
    if (!local || !domain) return '[REDACTED]';
    return `${local.substring(0, 2)}***@${domain}`;
  }

  private formatLogMessage(
    level: string,
    message: string,
    context?: LogContext,
  ): string {
    const timestamp = new Date().toISOString();
    const sanitizedContext = this.sanitizeContext(context);
    const contextStr =
      Object.keys(sanitizedContext).length > 0
        ? ` | Context: ${JSON.stringify(sanitizedContext)}`
        : '';

    return `[${timestamp}] ${level.toUpperCase()}: ${message}${contextStr}`;
  }

  info(message: string, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);

    if (this.showLogs) {
      console.info(this.formatLogMessage('info', message, sanitizedContext));
    }

    // Send to Sentry as breadcrumb for context
    Sentry.addBreadcrumb({
      message,
      data: sanitizedContext,
      level: 'info',
      category: 'peikko.activation',
    });
  }

  warn(message: string, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);

    if (this.showLogs) {
      console.warn(this.formatLogMessage('warn', message, sanitizedContext));
    }

    // Send to Sentry as breadcrumb and capture message
    Sentry.addBreadcrumb({
      message,
      data: sanitizedContext,
      level: 'warning',
      category: 'peikko.activation',
    });

    Sentry.captureMessage(message, 'warning');
  }

  error(message: string, error?: Error, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);

    if (this.showLogs) {
      console.error(this.formatLogMessage('error', message, sanitizedContext));
      if (error) {
        console.error('Error details:', error);
      }
    }

    // Set context for Sentry
    Sentry.withScope((scope) => {
      scope.setTag('component', 'peikko.activation');

      Object.entries(sanitizedContext).forEach(([key, value]) => {
        scope.setContext(key, value);
      });

      if (error) {
        scope.setExtra('originalMessage', message);
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message, 'error');
      }
    });
  }
}

// Export singleton instance
export const logger: ILogger = new PeikkoLogger();

// Specific loggers for various processes
export const Logger = {
  // Activation process logging
  voucherValidationStarted: (voucherCode: string, locale: string) => {
    // Always log this for debugging in dev env
    console.log(
      `[DEBUG] voucherValidationStarted called: ${voucherCode.substring(
        0,
        2,
      )}***, locale: ${locale}`,
    );
    logger.info('Voucher validation started', {
      voucherCode,
      locale,
      step: 'validation_start',
    });
  },

  voucherValidationSuccess: (
    voucherCode: string,
    productType: string,
    locale: string,
  ) => {
    logger.info('Voucher validation successful', {
      voucherCode,
      productType,
      locale,
      step: 'validation_success',
    });
  },

  voucherValidationFailed: (
    voucherCode: string,
    error: Error,
    locale: string,
    errorStatus?: number,
  ) => {
    logger.error('Voucher validation failed', error, {
      voucherCode,
      locale,
      errorStatus,
      step: 'validation_error',
    });
  },

  userActivationStarted: (voucherCode: string, locale: string) => {
    logger.info('User activation started', {
      voucherCode,
      locale,
      step: 'activation_start',
    });
  },

  userActivationSuccess: (
    voucherCode: string,
    locale: string,
    duration?: number,
  ) => {
    logger.info('User activation successful', {
      voucherCode,
      locale,
      duration,
      step: 'activation_success',
    });
  },

  userActivationFailed: (
    voucherCode: string,
    error: Error,
    locale: string,
    errorStatus?: number,
  ) => {
    logger.error('User activation failed', error, {
      voucherCode,
      locale,
      errorStatus,
      step: 'activation_error',
    });
  },

  tokenRenewalAttempt: (reason: string) => {
    logger.warn('Token renewal attempt', {
      reason,
      step: 'token_renewal',
    });
  },

  redirectToToosa: (
    redirectUrl: string,
    voucherCode: string,
    locale: string,
  ) => {
    logger.info('Redirecting to Toosa after successful activation', {
      redirectUrl,
      locale,
      voucherCode,
      step: 'redirect_to_toosa',
    });
  },
};
